// Apolo IDE — Cowork composer (E3): tabs strip over the open workspace panes.
//
// Each open session/folder = a tab. Click focuses its pane; × closes it. Reuses
// the EXISTING pane store (workspacePanes / focusedPaneId / focusPane / closePane)
// — no new "Workspace" model. Pure + props-driven so it's testable without the
// store. Renders nothing when there are no open panes.

import { X } from 'lucide-react';
import { sessionRoot } from '../../lib/sessionRoot.js';

// Tab label = session name, else the picked folder's basename, else a fallback.
// eslint-disable-next-line react-refresh/only-export-components -- util kept here for its co-located test (repo convention, cf. SidePanel.jsx/RunDetail.jsx)
export function tabLabel(session) {
  if (!session) return 'Sessão';
  if (session.name) return session.name;
  const root = sessionRoot(session);
  const base = root ? root.split('/').filter(Boolean).pop() : '';
  return base || 'Sessão';
}

export default function WorkspaceTabsBar({ panes = [], focusedPaneId, sessions = [], onFocus, onClose }) {
  if (!Array.isArray(panes) || panes.length === 0) return null;
  return (
    <div
      role="tablist"
      aria-label="Abas de workspace"
      className="flex shrink-0 items-stretch gap-1 overflow-x-auto border-b border-apolo-borderSubtle bg-apolo-surface1/80 px-2 py-1 backdrop-blur-md"
    >
      {panes.map((pane) => {
        const session = sessions.find((s) => s.id === pane.sessionId) || null;
        const focused = pane.id === focusedPaneId;
        const label = tabLabel(session);
        return (
          <div
            key={pane.id}
            role="tab"
            aria-selected={focused}
            tabIndex={0}
            onClick={() => onFocus?.(pane.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFocus?.(pane.id);
              }
            }}
            title={session ? sessionRoot(session) || label : label}
            className={`group inline-flex max-w-[220px] shrink-0 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 ${
              focused
                ? 'border-apolo-accent/40 bg-apolo-accent/12 text-apolo-textPrimary'
                : 'border-transparent text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
            }`}
          >
            <span className="truncate">{label}</span>
            <button
              type="button"
              aria-label={`Fechar aba ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose?.(pane.id);
              }}
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-apolo-textTertiary opacity-60 transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary group-hover:opacity-100"
            >
              <X size={11} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
